import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { tokens } from "../theme";

interface HeaderButtonProps {
  text: string;
  sectionId: string;
  page?: 'landing' | 'menu' | 'partners';
  active?: boolean;
  fullWidth?: boolean;
};

/**
 * Scrolls to a landing section, or navigates home from a sub-page.
 *
 * Scroll offsetting is handled by `scroll-margin-top` in index.css rather than
 * by measuring the header here — the browser applies it to every scroll into
 * that element, including native anchor jumps and focus moves.
 */
const HeaderButton = ({ text, sectionId, page = 'landing', active = false, fullWidth = false }: HeaderButtonProps) => {
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    if (id === 'hero-section') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleClick = () => {
    if (sectionId === 'hero-section' && page !== 'landing') {
      navigate('/');
      return;
    }
    scrollToSection(sectionId);
  };

  return (
    <Button
      onClick={handleClick}
      aria-current={active ? 'true' : undefined}
      fullWidth={fullWidth}
      sx={{
        position: 'relative',
        color: active ? tokens.color.roseInk : 'text.primary',
        fontWeight: active ? 700 : 500,
        fontSize: '1rem',
        px: 1.75,
        py: 1,
        minWidth: 'auto',
        justifyContent: fullWidth ? 'flex-start' : 'center',
        borderRadius: tokens.radius.pill,
        transition: `color ${tokens.duration.control} ease, background-color ${tokens.duration.control} ease, transform ${tokens.duration.press} ${tokens.ease.out}`,

        // An underline that grows from the centre, rather than a filled pill.
        // The nav sits over photography; a solid hover block fights the image.
        // Scaled rather than widened — animating `width` would relayout on every
        // frame, where a transform stays on the compositor.
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 14,
          right: 14,
          bottom: 6,
          height: '2px',
          borderRadius: '2px',
          backgroundColor: tokens.color.roseInk,
          transform: active ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'center',
          transition: `transform ${tokens.duration.control} ${tokens.ease.out}`,
        },

        // Hover is gated so a tap on a touch device does not leave the button
        // stuck in its hover state.
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: 'transparent',
            color: tokens.color.roseInk,
          },
          '&:hover::after': {
            transform: 'scaleX(1)',
          },
        },
      }}
    >
      {text}
    </Button>
  );
};

export default HeaderButton;
